// AtriumShell.tsx — Atrium two-tier app shell (Sprint 82, ADR-082).
// ----------------------------------------------------------------------------
// Full-viewport, overflow-hidden layout:
//
//   ┌──────┬────────────┬──────────────────────────────────┐
//   │ rail │  context   │  top bar (62px)                   │
//   │ 84px │  panel     ├──────────────────────────────────┤
//   │      │  236px     │  scroll area (children)           │
//   └──────┴────────────┴──────────────────────────────────┘
//
// It is a PURE presentational shell: it owns no routing or fetch logic. The
// orchestrator (MainDashboard) passes the already-filtered menu items + the
// shared selected-page state, and renders the SAME DashboardRouter as children.
// This keeps the Atrium shell additive and reversible behind the feature flag.
import React, { useMemo } from 'react';
import { ConfigProvider } from 'antd';
import '../../styles/atrium-tokens.css';
import '../../styles/atrium-tables.css';

import { atriumAntdTheme } from '../../../../theme/theme';
import { NavRail } from './NavRail';
import { NavPanel, type NavPanelProgressCard } from './NavPanel';
import { AtriumTopBar, type AtriumCondoOption } from './AtriumTopBar';
import { buildSections, sectionForSelected } from './navModel';
import type { MenuItem, MenuGroupKey, DashboardComponent } from '../dashboard/menuConfig';
import type { RouteStatus } from '../dashboard/hooks/useMenuState';

export interface AtriumShellProps {
  /** Already role/feature-filtered menu items (from useMenuState). */
  filteredItems: MenuItem[];
  selected:      DashboardComponent;
  onSelect:      (key: DashboardComponent) => void;
  /**
   * Sprint 137 (ADR-132) — how the URL resolved (from useMenuState, ADR-128).
   * The shell needs this because on an error route `selected` deliberately
   * still points at the role default (it drives nav highlighting), so it is a
   * lie about what is on screen. Optional: the legacy shell doesn't pass it,
   * and omitting it keeps the pre-137 behaviour.
   */
  routeStatus?:  RouteStatus;
  role:          string | undefined;
  avatar:        string;
  onQuickCreate?: () => void;
  onBell?:        () => void;
  hasAlerts?:     boolean;
  // Sprint 90 — bell panel "View all" deep-link to the full Notification Center.
  onViewAllNotifications?: () => void;
  progressCard?:  NavPanelProgressCard;
  // Sprint 88A — top-bar shell actions (avatar menu + property switcher).
  onProfile?:       () => void;
  onLogout?:        () => void;
  availableCondos?: AtriumCondoOption[];
  currentCondo?:    AtriumCondoOption | null;
  onSwitchCondo?:   (condoId: string) => void;
  children:       React.ReactNode;
}

const appStyle: React.CSSProperties = {
  display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg)',
};

const mainStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%',
};

const scrollStyle: React.CSSProperties = {
  flex: 1, overflowY: 'auto', background: 'var(--bg)',
};

/**
 * Sprint 137 (ADR-132) — what the chrome should call an error route. These are
 * deliberately close to the on-screen <Result> titles (RouteNotFound.tsx): the
 * whole point is that the title, the announcement, and the page agree.
 */
const ROUTE_ERROR_LABELS: Partial<Record<RouteStatus, string>> = {
  'not-found': 'Page not found',
  forbidden:   'Access denied',
};

export const AtriumShell: React.FC<AtriumShellProps> = ({
  filteredItems, selected, onSelect, role, avatar, routeStatus,
  onQuickCreate, onBell, hasAlerts, progressCard,
  onProfile, onLogout, availableCondos, currentCondo, onSwitchCondo,
  onViewAllNotifications,
  children,
}) => {
  const sections = useMemo(() => buildSections(filteredItems), [filteredItems]);
  const activeSection = useMemo(
    () => sectionForSelected(sections, selected),
    [sections, selected],
  );

  // F4 — flatten the (already filtered) sections into quick-nav targets for the
  // top-bar command palette. Selecting one reuses the shell's onSelect, so it
  // navigates exactly like clicking the page in the nav panel.
  const navTargets = useMemo(
    () => sections.flatMap(s => s.items.map(i => ({
      key:     i.key,
      label:   i.label,
      section: s.label,
    }))),
    [sections],
  );

  // Clicking a rail icon selects the section's first sub-page.
  const onSection = (key: MenuGroupKey) => {
    const s = sections.find(x => x.key === key);
    if (s && s.items.length) onSelect(s.items[0].key);
  };

  // Sprint 137 (ADR-132) — R11-8 (ADR-128) taught the router to render an
  // honest 404/403, but left the chrome deriving its label from the nav's
  // `selected` key, which on an error route still holds the role default. The
  // shell therefore titled a 404 "My Home · CondoPro" and announced "Navigated
  // to My Home" — telling a screen-reader user they arrived somewhere they
  // demonstrably had not. The error status has to win over `selected`.
  const errorLabel = routeStatus ? ROUTE_ERROR_LABELS[routeStatus] : undefined;
  const pageLabel =
    errorLabel ?? activeSection?.items.find(i => i.key === selected)?.label ?? 'Dashboard';

  // SPA route-change announcement (WCAG 4.1.3 / 2.4.2). The Atrium shell owns no
  // router, so when the shared `selected` page key changes we (a) keep the
  // document.title in sync and (b) push a polite live-region message. Screen
  // readers announce "Navigated to <Page>" without stealing focus.
  //
  // On an error route we announce the error verbatim rather than "Navigated to
  // Page not found" — the user did not navigate to a page; there isn't one.
  const [announcement, setAnnouncement] = React.useState('');
  React.useEffect(() => {
    if (pageLabel) {
      document.title = `${pageLabel} · CondoPro`;
      setAnnouncement(errorLabel ? errorLabel : `Navigated to ${pageLabel}`);
    }
  }, [pageLabel, errorLabel]);

  // Sprint 147 (ADR-142, R14-29) — the shell OWNS the title while it is
  // mounted, so it must give it back when it unmounts. Without this, an
  // expired session that bounced from the Document Centre left the login
  // screen titled "Document Center · CondoPro" — the tab asserting the user
  // was on a page they had just been logged out of. Separate from the effect
  // above deliberately: a cleanup there would fire on every page change.
  React.useEffect(() => () => { document.title = 'CondoPro'; }, []);

  return (
    // Nested ConfigProvider: re-skins ALL AntD primitives (Table/Button/Tag/
    // Input/Select/Modal) rendered inside the Atrium shell to the #2563EB blue +
    // Onest + Atrium radii. Scoped to the shell, so the legacy global theme
    // (antdTheme, #6366f1) is untouched when the flag is off. (Sprint 85)
    <ConfigProvider theme={atriumAntdTheme}>
    <div className="atrium" style={appStyle}>
      {/* Skip-to-content (WCAG 2.4.1): first focusable element, visually hidden
          until focused, jumps past the two-tier nav to the main scroll area. */}
      <a href="#atrium-main" className="atrium-skip-link">Skip to main content</a>
      <NavRail
        sections={sections}
        activeKey={activeSection?.key}
        onSection={onSection}
        avatar={avatar}
      />
      <NavPanel
        section={activeSection}
        selected={selected}
        onSelect={onSelect}
        onQuickCreate={onQuickCreate}
        progressCard={progressCard}
      />
      <div style={mainStyle}>
        <AtriumTopBar
          sectionLabel={activeSection?.label ?? 'CondoPro'}
          pageLabel={pageLabel}
          role={role}
          avatar={avatar}
          hasAlerts={hasAlerts}
          onBell={onBell}
          onViewAllNotifications={onViewAllNotifications}
          onProfile={onProfile}
          onLogout={onLogout}
          availableCondos={availableCondos}
          currentCondo={currentCondo}
          onSwitchCondo={onSwitchCondo}
          navTargets={navTargets}
          onNavTarget={onSelect}
        />
        {/* Main landmark (WCAG 1.3.1) + skip-link target. tabIndex -1 so the
            skip link can move focus here. */}
        <main id="atrium-main" tabIndex={-1} aria-label={pageLabel} style={scrollStyle}>
          <div style={{ padding: '26px 30px 44px', maxWidth: 1480 }}>
            {children}
          </div>
        </main>
      </div>

      {/* Polite live region for SPA route-change announcements (WCAG 4.1.3). */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
          overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
        }}
      >
        {announcement}
      </div>
    </div>
    </ConfigProvider>
  );
};
